import { Dialog, Icon } from '@blueprintjs/core'
import React from 'react'
import style from './BotsLimitExceededDialog.scss'

interface Bot {
  id: string
  name: string
  description?: string
  languages?: string[]
  messaging?: {
    channels?: {
      [key: string]: any
    }
  }
}

interface BotsLimitExceededDialogProps {
  isOpen: boolean
  filteredBots: Bot[]
  selectedBotIds: string[]
  maxBotsAllowed?: number
  onCheckboxChange: (botId: string) => void
  onConfirmSelectedBots: () => void
}

export const BotsLimitExceededDialog: React.FC<BotsLimitExceededDialogProps> = ({
  isOpen,
  filteredBots = [],
  selectedBotIds = [],
  maxBotsAllowed = 3,
  onCheckboxChange,
  onConfirmSelectedBots
}) => {
  const botsToRemoveCount = Math.max(filteredBots.length - maxBotsAllowed, 0)
  const isDeleteDisabled = filteredBots.length - selectedBotIds.length > maxBotsAllowed
  const remainingNeeded = Math.max(botsToRemoveCount - selectedBotIds.length, 0)

  return (
    <Dialog
      isOpen={isOpen}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className={style.titleIconBadge}>
            <Icon icon="warning-sign" iconSize={20} />
          </div>
          <span style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
            Bots Limit Exceeded
          </span>
        </div>
      }
      className={style.dialogContainer}
    >
      <div className={style.bodyContainer}>
        <div className={style.scrollableContent}>
          <div className={style.banner}>
            <p>
              Your current plan allows a maximum of <strong>{maxBotsAllowed} active bots</strong>.
              You are currently managing <strong>{filteredBots.length} bots</strong>.
              Please select and remove at least <strong>{botsToRemoveCount} bot(s)</strong> to continue.
            </p>
          </div>

          <div className={style.instructionHeader}>
            <div className={style.instructionTitle}>
              Select bots to remove (click card to select)
            </div>
            <div className={style.instructionSub}>
              {remainingNeeded > 0 ? (
                <span className={style.pendingText}>
                  Select <strong>{remainingNeeded}</strong> more bot(s)
                </span>
              ) : (
                <span className={style.readyText}>
                  Ready ({selectedBotIds.length} selected)
                </span>
              )}
            </div>
          </div>

          <div className={style.botsListContainer}>
            {filteredBots && filteredBots.length ? (
              filteredBots.map(bot => {
                const isSelected = selectedBotIds.includes(bot.id)
                const rawChannel = bot.messaging?.channels ? Object.keys(bot.messaging.channels)[0] : 'webchat'
                const channel = rawChannel === 'twilio' ? 'WhatsApp' : rawChannel.charAt(0).toUpperCase() + rawChannel.slice(1)

                return (
                  <div
                    key={bot.id}
                    className={`${style.botCard} ${isSelected ? style.selected : ''}`}
                    onClick={() => onCheckboxChange(bot.id)}
                  >
                    <div className={style.botCardHeader}>
                      <div className={style.botIconContainer}>
                        <Icon icon={isSelected ? 'trash' : 'chat'} iconSize={18} />
                      </div>
                      <div className={style.cardBadgesRow}>
                        <span className={style.channelBadge}>{channel}</span>
                        <div className={`${style.checkboxContainer} ${isSelected ? style.selected : ''}`}>
                          {isSelected && <Icon icon="tick" iconSize={12} />}
                        </div>
                      </div>
                    </div>

                    <div className={style.botDetails}>
                      <h3 className={style.botName}>{bot.name}</h3>
                      {bot.description && (
                        <p className={style.botDescription}>{bot.description}</p>
                      )}
                      <div className={style.botMetaRow}>
                        <div className={style.botMetaItem}>
                          <strong>ID:</strong> {bot.id}
                        </div>
                        {bot.languages && bot.languages.length > 0 && (
                          <div className={style.botMetaItem}>
                            <strong>Lang:</strong>{' '}
                            {bot.languages.map(l => l.toUpperCase()).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className={style.emptyBots}>
                <Icon icon="info-sign" iconSize={24} />
                <p>No bots available</p>
              </div>
            )}
          </div>
        </div>

        <div className={style.actionsContainer}>
          <button
            className={style.deleteBtn}
            disabled={isDeleteDisabled}
            onClick={onConfirmSelectedBots}
          >
            {isDeleteDisabled
              ? `Select ${remainingNeeded} more bot(s) to remove`
              : `Remove Selected Bots (${selectedBotIds.length})`}
          </button>
        </div>
      </div>
    </Dialog>
  )
}

export default BotsLimitExceededDialog
